/**
 * DictationOverlay — Telegram-style "recording / transcribing" pill
 * that takes over the textarea slot in {@link ChatInput} while the
 * user is dictating.
 *
 * Layouts a red recording dot, a mm:ss timer, a rolling RMS waveform
 * driven by {@link useDictation}'s ``levels`` array, and a cancel (✕)
 * button. The "send" half of the action (stop + decode + append) is
 * handled by the existing mic toggle in ChatInput, so this overlay
 * only owns the cancel control.
 *
 * Two states are rendered:
 *   - ``recording``: live waveform, timer counting up
 *   - ``finalizing``: waveform freezes (last samples), label flips to
 *     "Transcribing…" with a subtle pulse so the user knows the
 *     model is decoding rather than the UI being stuck
 */

import { DICTATION_LEVEL_BARS } from "@/hooks/useDictation";

export interface DictationOverlayProps {
  /** RMS levels [0, 1], length :data:`DICTATION_LEVEL_BARS`. */
  levels: number[];
  /** Milliseconds since recording started. */
  elapsedMs: number;
  /** True while the backend is decoding (status === "finalizing"). */
  finalizing: boolean;
  /** Discard the recording without transcribing. */
  onCancel: () => void;
}

function formatElapsed(ms: number): string {
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function DictationOverlay({
  levels,
  elapsedMs,
  finalizing,
  onCancel,
}: DictationOverlayProps) {
  // Guard: levels may briefly be the wrong length during the first
  // render after start(). Pad/truncate so the bar grid never jumps.
  const safeLevels =
    levels.length === DICTATION_LEVEL_BARS
      ? levels
      : [
          ...new Array(Math.max(0, DICTATION_LEVEL_BARS - levels.length)).fill(0),
          ...levels.slice(-DICTATION_LEVEL_BARS),
        ];

  return (
    <div
      className="flex items-center gap-3 w-full py-2 pr-1 min-h-[40px]"
      role="status"
      aria-live="polite"
      aria-label={finalizing ? "Transcribing" : "Recording"}
    >
      {/* Cancel (✕) — closest to the textarea-left edge so it reads as
          "go back to typing", not "delete the message". */}
      <button
        type="button"
        onClick={onCancel}
        className="shrink-0 p-1.5 rounded-lg text-space-40 hover:text-hydra hover:bg-hydra-50/30 transition-colors"
        title="Cancel dictation"
        aria-label="Cancel dictation"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Recording dot + label. Dot turns from solid-pulsing red
          (recording) to spinner-style (finalizing) so the state change
          is unambiguous even with the waveform frozen. */}
      <div className="flex items-center gap-2 shrink-0">
        {finalizing ? (
          <svg
            className="h-3 w-3 animate-spin text-jove"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        ) : (
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-hydra opacity-60 animate-ping" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-hydra" />
          </span>
        )}
        <span
          className={`text-xs font-medium tabular-nums ${
            finalizing ? "text-jove" : "text-space-200"
          }`}
        >
          {finalizing ? "Transcribing…" : formatElapsed(elapsedMs)}
        </span>
      </div>

      {/* Rolling waveform. Bars are right-aligned so new samples
          appear on the right and slide left — same convention as
          Telegram / WhatsApp voice notes. */}
      <div
        className={`flex-1 flex items-center justify-end gap-[2px] h-7 transition-opacity ${
          finalizing ? "opacity-50" : "opacity-100"
        }`}
        aria-hidden="true"
      >
        {safeLevels.map((lvl, i) => {
          // Minimum bar height keeps the meter "alive" even in silence;
          // max caps to ~24 px so loud bursts don't blow the row out.
          const height = Math.max(2, Math.round(lvl * 24));
          return (
            <span
              key={i}
              className={`w-[3px] rounded-full ${
                finalizing ? "bg-space-30" : "bg-jove"
              }`}
              style={{ height: `${height}px` }}
            />
          );
        })}
      </div>
    </div>
  );
}
